import { useEffect, useState } from "react";
import AccountLayout, { SettingsRow, SettingsSection } from "../components/AccountLayout";
import { ThemePreferenceSelect } from "../components/ThemeToggle";
import { useTheme } from "@/hooks/useTheme";
import { useTimezoneStore, type TimeZoneMode } from "@/stores/timezone";
import { formatClock } from "@/lib/dates";

const TZ_OPTIONS: { id: TimeZoneMode; label: string }[] = [
  { id: "utc", label: "UTC" },
  { id: "local", label: "Local" },
];

function localZoneName(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "your device's time zone";
  } catch {
    return "your device's time zone";
  }
}

/** Segmented UTC / Local control; same store as the sidebar clock. */
function TimeZoneSelect() {
  const mode = useTimezoneStore((s) => s.mode);
  const setMode = useTimezoneStore((s) => s.setMode);
  return (
    <div role="radiogroup" aria-label="Time zone" className="inline-flex shrink-0 rounded-[10px] border border-[var(--border)] p-0.5">
      {TZ_OPTIONS.map(({ id, label }) => {
        const active = mode === id;
        return (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={active}
            className={`inline-flex items-center rounded-lg px-2.5 py-1.5 text-sm transition-colors ${
              active
                ? "bg-[var(--bg-hover)] font-medium text-[var(--text-primary)]"
                : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            }`}
            onClick={() => setMode(id)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/** Current time in the chosen zone, ticking every second, as a preview of the setting. */
function ClockPreview({ mode }: { mode: TimeZoneMode }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <span className="tabular-nums text-[var(--text-primary)]">
      {formatClock(now, mode)} {mode === "utc" ? "UTC" : localZoneName()}
    </span>
  );
}

export default function AccountPreferences() {
  const { theme, preference } = useTheme();
  const tzMode = useTimezoneStore((s) => s.mode);

  const themeHint =
    preference === "system"
      ? `Follows your device, now ${theme === "dark" ? "dark" : "light"}.`
      : `Always ${theme === "dark" ? "dark" : "light"}.`;

  return (
    <AccountLayout>
      <SettingsSection title="Appearance" description="Saved on this device.">
        <SettingsRow label="Theme" hint={themeHint}>
          <ThemePreferenceSelect />
        </SettingsRow>
      </SettingsSection>

      <SettingsSection title="Time & dates" description="Applies to charts, trades, orders and backtest ranges.">
        <SettingsRow
          label="Time zone"
          hint={tzMode === "utc" ? "Same as the exchanges." : `Your device's zone, ${localZoneName()}.`}
        >
          <TimeZoneSelect />
        </SettingsRow>
        <SettingsRow label="Preview" hint="How times look with this setting.">
          <span className="text-sm">
            <ClockPreview mode={tzMode} />
          </span>
        </SettingsRow>
      </SettingsSection>
    </AccountLayout>
  );
}
